import {
  of,
  concat,
  fromEvent,
  EMPTY
} from "rxjs";
import {
  mergeMap,
  map,
  debounceTime,
  catchError,
  takeUntil,
  filter,
  take,
} from "rxjs/operators";
import { ajax } from 'rxjs/ajax';
import { isEmpty, isNil, path } from "ramda";
import { combineEpics, ofType } from "redux-observable";
import { API_BASE_URL } from "props";
import { ajaxGetAllModules, apiAjax, errorToAction } from "util/ajaxUtil";
import * as applicationActionTypes from "application/redux/applicationActionTypes";
import * as manageModulesActionTypes from "manage-modules/redux/manageModulesActionTypes";
import {
  applyFilterQuery,
  fetchModules,
  moduleFieldSynced,
  moduleItemsLoaded,
  moduleItemsSynced,
  moduleThumbnailSynced,
  modulesLoaded,
  newModuleCardInternalCodeSuggested,
  newModulePublished,
  selectModule,
  showModuleThumbnailUrl,
  showNewModuleThumbnailUrl,
} from "manage-modules/redux/manageModulesActions";

const MANAGE_MODULES_PATH = "/manage-modules"; //TODO: make common;
const POST_NEW_MODULE_URL = `${API_BASE_URL}/admin/modules`;
const POST_SUGGEST_INTERNAL_CODE_URL = `${API_BASE_URL}/admin/modules/suggest-internal-code`;
const getModuleMediaUrl = moduleId =>
  `${API_BASE_URL}/admin/modules/${moduleId}/media?limit=all`;
const putModuleItemsUrl = moduleId =>
  `${API_BASE_URL}/admin/modules/${moduleId}/media`;
const patchModuleUrl = moduleId => `${API_BASE_URL}/admin/modules/${moduleId}`;

const getUploadThumbnailUrl = moduleId =>
  `${API_BASE_URL}/admin/modules/${moduleId}/thumbnail`;

const readFileUrl = file => {
  const reader = new FileReader();

  const read$ = fromEvent(reader, "load").pipe(
    map(e => e.target.result)
  );

  reader.readAsDataURL(file);

  return read$.pipe(
    take(1)
  );
};

const ajaxGetModuleItems = state => moduleId =>
  apiAjax(state)({
    url: getModuleMediaUrl(moduleId),
  }).pipe(
    map(ajaxResponse => ajaxResponse.response)
  );

const ajaxPatchModule = state => (moduleId, update) =>
  apiAjax(state)({
    url: patchModuleUrl(moduleId),
    method: "PATCH",
    body: update,
  }).pipe(
    map(response => update)
  );

// const ajaxPatchModule = state => (moduleId, update) =>
//     Observable.of(update)
//         .delay(1000)
//         .map(response => update);

const ajaxPostModuleThumbnail = state => (moduleId, file) => {
  const formData = new FormData();
  formData.append("file", file);

  return ajax({
    url: getUploadThumbnailUrl(moduleId),
    method: "POST",
    crossDomain: true,
    headers: {
      Authorization: `Bearer ${state.auth.user.token}`,
    },
    body: formData,
  });
};

// const ajaxPostModuleThumbnail = state => (moduleId, file) =>
//     Observable.of({moduleId, file})
//         .delay(1000);

const ajaxPostNewModule = state => module =>
  apiAjax(state)({
    url: POST_NEW_MODULE_URL,
    method: "POST",
    body: module,
  }).pipe(
    map(response => module)
  );

// const ajaxPostNewModule = state => module =>
//     Observable.of(module)
//         .delay(1000)
//         .map(response => module);

const ajaxPostSuggestInternalCode = state => internalCode =>
  apiAjax(state)({
    url: POST_SUGGEST_INTERNAL_CODE_URL,
    method: "POST",
    body: { internalCode },
  }).pipe(
    map(ajaxResponse => {
      return ajaxResponse.response ? ajaxResponse.response.internalCode : "";
    })
  );

const ajaxPutModuleItems = state => (moduleId, update) =>
  apiAjax(state)({
    url: putModuleItemsUrl(moduleId),
    method: "PUT",
    body: update,
  }).pipe(
    map(response => update)
  );

// const ajaxPutModuleItems = state => (moduleId, update) =>
//     Observable.of(update)
//         .delay(1000)
//         .map(response => update);

const canSyncModuleCardField = (state, field) => {
  const { moduleCard, syncingFields } = state.manageModules;
  return !isNil(path([moduleCard.module.id, field], syncingFields));
};

const canPublishNewModule = state => {
  const { moduleCard } = state.manageModules;
  return (
    isEmpty(moduleCard.errors) &&
    !moduleCard.fetchingInternalCodeSuggestion &&
    !moduleCard.fetchingBase64EncodedThumbnailUrl
  );
};

const changeNewModuleCardThumbnailEpic = (action$, store) =>
  action$.pipe(
    ofType(manageModulesActionTypes.CHANGE_NEW_MODULE_CARD_THUMBNAIL),
    mergeMap(action => {
      const { file } = action;

      return readFileUrl(file).pipe(
        map(url => showNewModuleThumbnailUrl(url)),
        catchError(errorToAction),
        takeUntil(action$.pipe(
          ofType(manageModulesActionTypes.SELECT_MODULE)
        ))
      ); // not sure I like relying on this cancel
    })
  );

const fetchModulesEpic = (action$, state$) =>
  action$.pipe(
    ofType(manageModulesActionTypes.FETCH_MODULES),
    mergeMap(() =>
        ajaxGetAllModules(state$.value).pipe(
          map(modulesLoaded),
          catchError(errorToAction),
          // cancel pending fetches if route changes
          takeUntil(action$.pipe(
            ofType(applicationActionTypes.ROUTE_CHANGED)
          ))
        )
    )
  );

const filterQueryEpic = action$ =>
  action$.pipe(
    ofType(manageModulesActionTypes.FILTER_QUERY_CHANGED),
    debounceTime(100),
    map(action => applyFilterQuery(action.filterQuery))
  );

// this probably shouldn't exist
const modulesLoadedEpic = (action$, state$) =>
  action$.pipe(
    ofType(manageModulesActionTypes.MODULES_LOADED),
    map(() => {
      const selectedModuleId = state$.value.manageModules.moduleCard.module.id;
      return selectModule(selectedModuleId);
    })
  );

const publishNewModuleEpic = (action$, state$) =>
  action$.pipe(
    ofType(manageModulesActionTypes.PUBLISH_NEW_MODULE),
    mergeMap(action => {
      const state = state$.value;

      if (canPublishNewModule(state)) {
        const { module } = state.manageModules.moduleCard;

        return ajaxPostNewModule(state)(module).pipe(
          mergeMap(() =>
              ajaxPostModuleThumbnail(state)(module.id, action.thumbnailFile),
          ),
          map(() => newModulePublished(module.id)),
          catchError(errorToAction)
      );
      } else {
        return EMPTY;
      }
    })
  );

const removeModuleCardItemsEpic = (action$, state$) =>
  action$.pipe(
    ofType(manageModulesActionTypes.REMOVE_MODULE_CARD_ITEMS),
    mergeMap(() => {
      const state = state$.value;
      const moduleCardState = state.manageModules.moduleCard;
      const moduleId = moduleCardState.module.id;
      const mediaIds = moduleCardState.items.map(item => item.id);

      return ajaxPutModuleItems(state)(moduleId, { ids: mediaIds }).pipe(
        map(moduleItemsSynced),
        catchError(errorToAction)
      );
    })
  );

const routeChangedEpic = (action$, store) =>
  action$.pipe(
    filter(action =>
      action.type === applicationActionTypes.ROUTE_CHANGED &&
      action.path === MANAGE_MODULES_PATH
    ),
    mergeMap(() => {
      return of(fetchModules());
    })
  );

const selectModuleEpic = (action$, state$) =>
  action$.pipe(
    ofType(manageModulesActionTypes.SELECT_MODULE),
    mergeMap(action =>
        ajaxGetModuleItems(state$.value)(action.moduleId).pipe(
          map(moduleItemsLoaded),
          catchError(errorToAction),
          takeUntil(action$.pipe(
            ofType(
              applicationActionTypes.ROUTE_CHANGED,
              manageModulesActionTypes.SELECT_MODULE
            )
          ))
      ),
  ));

const saveNewModuleCardFieldSuggestAsInternalCodeEpic = (action$, state$) =>
  action$.pipe(
    ofType(manageModulesActionTypes.SAVE_NEW_MODULE_CARD_FIELD_SUGGEST_AS_INTERNAL_CODE),
    mergeMap(action => {
      const state = state$.value;
      const { value } = action;

      const { fetchingInternalCodeSuggestion } = state.manageModules.moduleCard;

      if (fetchingInternalCodeSuggestion) {
        return ajaxPostSuggestInternalCode(state$.value)(value).pipe(
          map(newModuleCardInternalCodeSuggested),
          catchError(errorToAction),
          takeUntil(action$.pipe(
            ofType(
              manageModulesActionTypes.SAVE_NEW_MODULE_CARD_FIELD_SUGGEST_AS_INTERNAL_CODE,
              manageModulesActionTypes.SELECT_MODULE
            )
          ))
        ); // not sure I like relying on this cancel
      } else {
        return EMPTY;
      }
    })
  );

const syncModuleCardFieldEpic = (action$, state$) =>
  action$.pipe(
    ofType(manageModulesActionTypes.SYNC_MODULE_CARD_FIELD),
    mergeMap(action => {
      const state = state$.value;
      const { module } = state.manageModules.moduleCard;
      const { field, value } = action;

      if (canSyncModuleCardField(state, field)) {
        return ajaxPatchModule(state$.value)(module.id, { [field]: value }).pipe(
          map(() => moduleFieldSynced(module.id, field, value)),
          catchError(errorToAction)
        );
      } else {
        return EMPTY;
      }
    })
  );

const syncModuleCardInternalCodeEpic = (action$, state$) =>
  action$.pipe(
    ofType(manageModulesActionTypes.SYNC_MODULE_CARD_INTERNAL_CODE),
    mergeMap(action => {
      const state = state$.value;
      const { module } = state.manageModules.moduleCard;
      const { internalCode } = action;

      if (canSyncModuleCardField(state, "internalCode")) {
        return ajaxPostSuggestInternalCode(state$.value)(internalCode).pipe(
          mergeMap(suggestedInternalCode =>
            ajaxPatchModule(state$.value)(module.id, { internalCode: suggestedInternalCode }).pipe(
              map(() =>
                moduleFieldSynced(
                  module.id,
                  "internalCode",
                  suggestedInternalCode
                )
              )
            )
          ),
          catchError(errorToAction)
        );
      } else {
        return EMPTY;
      }
    })
  );

const syncModuleCardThumbnailEpic = (action$, state$) =>
  action$.pipe(
    ofType(manageModulesActionTypes.SYNC_MODULE_CARD_THUMBNAIL),
    mergeMap(action => {
      const { file } = action;
      const { module } = state$.value.manageModules.moduleCard;

      return concat(
        readFileUrl(file).pipe(
          map(url =>
            showModuleThumbnailUrl(module.id, url)
          )
        ),
        ajaxPostModuleThumbnail(state$.value)(module.id, file).pipe(
          map(() => moduleThumbnailSynced(module.id))
        )
      ).pipe(
        catchError(errorToAction)
      );
    })
  );

export default combineEpics(
  changeNewModuleCardThumbnailEpic,
  fetchModulesEpic,
  filterQueryEpic,
  modulesLoadedEpic,
  publishNewModuleEpic,
  removeModuleCardItemsEpic,
  routeChangedEpic,
  saveNewModuleCardFieldSuggestAsInternalCodeEpic,
  selectModuleEpic,
  syncModuleCardFieldEpic,
  syncModuleCardInternalCodeEpic,
  syncModuleCardThumbnailEpic,
);
